import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import { ApolloClient } from '@apollo/client';

import { ENABLE_CHAT, ENABLE_LOGROCKET, ENABLE_SENTRY, HAS_PHI } from '../../../_configuration';
import ChatWoot from './chat.service';
import { UPDATE_USER_SESSION_LOGROCKET } from '../createApolloClient';
import { forwardRedactedConsole, redactText, sanitizeRequest, sanitizeResponse } from './logrocket.sanitizers';

interface SessionInfo {
  _id: string;
  email: string;
  name: string;
}

if (ENABLE_LOGROCKET && !HAS_PHI) {
  LogRocket.init('qf64cz/max-analytics', {
    console: { isEnabled: false },
    network: { requestSanitizer: sanitizeRequest, responseSanitizer: sanitizeResponse },
    browser: { urlSanitizer: redactText },
  });
  forwardRedactedConsole(LogRocket);
}

export class SessionReporter {
  private static logRocketSessionId: string | null = null;
  private static apolloClient: ApolloClient<any> | null = null;

  static identify(user: SessionInfo, traits: object = {}) {
    console.log('Identifying logrocket user:', {user});
    if (ENABLE_SENTRY && !HAS_PHI) {
      Sentry.setUser({ email: user.email });
    }

    if (ENABLE_LOGROCKET && !HAS_PHI) {
      LogRocket.identify(user._id, {
        ...traits,
        name: user.name,
        email: user.email,
      });

      // Wait for LogRocket session to be ready, then create user session
      LogRocket.getSessionURL((sessionURL) => {
        console.log('LogRocket session URL:', sessionURL);
        if (sessionURL) {
          this.logRocketSessionId = sessionURL.split('/').pop() || null;
          console.log('LogRocket session ID captured:', this.logRocketSessionId);

          // Update existing user session on the server with LogRocket session ID
          this.updateUserSessionWithLogRocket();
        }
      });
    }

    if (ENABLE_CHAT && !HAS_PHI) {
      ChatWoot.setUser(user._id, {
        email: user.email,
        name: user.name
      });
    }
  }

  private static async updateUserSessionWithLogRocket() {
    if (!this.apolloClient || !this.logRocketSessionId) {
      console.warn('Cannot update user session: Apollo client or LogRocket session ID not available', {
        hasClient: !!this.apolloClient,
        hasSessionId: !!this.logRocketSessionId
      });
      return;
    }

    try {
      const result = await this.apolloClient.mutate({
        mutation: UPDATE_USER_SESSION_LOGROCKET,
        variables: {
          logRocketSessionId: this.logRocketSessionId
        }
      });

      console.log('User session updated with LogRocket session ID on server:', {
        logRocketSessionId: this.logRocketSessionId,
        userSession: result.data?.updateUserSessionLogRocket
      });
    } catch (error) {
      console.warn('Failed to update user session with LogRocket session ID on server:', error);
    }
  }

  static setApolloClient(client: ApolloClient<any>) {
    this.apolloClient = client;
  }

  static getLogRocketSessionId(): string | null {
    return this.logRocketSessionId;
  }

  static logout() {
    if (ENABLE_SENTRY) {
      Sentry.configureScope(scope => scope.setUser(null));
    }

    if (ENABLE_CHAT) {
      ChatWoot.clearUser();
    }
  }
}
